import { Service, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxConfirmModalData,
  OnyxDropdownOptionsSource,
  OnyxOption,
  OnyxOptionsGroup,
} from '@onyx/angular';
import {
  OnyxConfirmModalComponent,
  OnyxFormMode,
  OnyxModalService,
  OnyxNamePipe,
  OnyxToastService,
} from '@onyx/angular';
import { isString, orderBy } from 'lodash';
import { map } from 'rxjs';
import { AuthStore } from '../../../../../auth/common/stores/auth.store';
import { UnavailabilityType } from '../../../../../common/components/unavailabilities/unavailabilities.component';
import type { UnavailabilityModalData } from '../../../../../common/components/unavailabilities/unavailability-modal/unavailability-modal.component';
import { UnavailabilityModalComponent } from '../../../../../common/components/unavailabilities/unavailability-modal/unavailability-modal.component';
import { CommonHelper } from '../../../../../common/helpers/common.helper';
import type { HelperOptions } from '../../../../../common/interfaces/utilities/helper-options';
import { FleetHelper } from '../../../../fleet/common/helpers/fleet.helper';
import type { EmployeeModalData } from '../../employees-list/employee-modal/employee-modal.component';
import { EmployeeModalComponent } from '../../employees-list/employee-modal/employee-modal.component';
import type { EditEmployeeModalData } from '../components/edit-employee-modal/edit-employee-modal.component';
import { EditEmployeeModalComponent } from '../components/edit-employee-modal/edit-employee-modal.component';
import type { EmployeeAssignVehiclesModalData } from '../components/employee-assign-vehicles-modal/employee-assign-vehicles-modal.component';
import { EmployeeAssignVehiclesModalComponent } from '../components/employee-assign-vehicles-modal/employee-assign-vehicles-modal.component';
import { EMPLOYEE_ROLE_LEVELS } from '../constants/employee-role-levels';
import { EmployeeCategory } from '../enums/employee-category';
import { EmployeeRole } from '../enums/employee-role';
import { EmployeeStatus } from '../enums/employee-status';
import type { Employee, SimplifiedEmployee } from '../interfaces/employee';
import { EmployeesService } from '../services/employees.service';

export const EMPLOYEE_HEADER_ACTIONS = ['edit'] as const;

@Service()
export class EmployeeHelper {
  private readonly authStore = inject(AuthStore);
  private readonly fleetHelper = inject(FleetHelper);
  private readonly employeesService = inject(EmployeesService);
  private readonly modalService = inject(OnyxModalService);
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(OnyxToastService);
  private readonly namePipe = inject(OnyxNamePipe);

  private readonly I18N = 'employees.employeesList';

  public getEmployeesSource(options?: {
    sort: boolean;
  }): OnyxDropdownOptionsSource<Employee> {
    const { sort = false } = options ?? {};

    return {
      list: (query, pagination) =>
        this.employeesService
          .searchEmployees(query, {
            ...pagination,
            category: EmployeeCategory.EMPLOYED,
            roles: [],
            statuses: [EmployeeStatus.ACTIVE, EmployeeStatus.UNAVAILABLE],
          })
          .pipe(
            map(({ options, totalItems }) => {
              if (options == null) return { options, totalItems };

              const sortByLastName = (items: OnyxOption<Employee>[]) =>
                orderBy(items, (option) => option.name.split(' ')[1], 'asc');
              return {
                options: sort
                  ? options.every((option) => 'options' in option)
                    ? options.map(({ options, ...headings }) => ({
                        ...headings,
                        options: sortByLastName(options),
                      }))
                    : sortByLastName(options)
                  : options,
                totalItems,
              };
            }),
          ),
      get: (uuid) =>
        this.employeesService.getEmployee(uuid).pipe(
          map((employee) => ({
            name: this.namePipe.transform(employee),
            value: employee,
          })),
        ),
      idKey: 'uuid',
    };
  }

  public getOptions(
    employee: Employee,
    options: HelperOptions,
    exclude: readonly (typeof EMPLOYEE_HEADER_ACTIONS)[number][] = [],
  ): OnyxOptionsGroup<() => void>[] {
    const hasEdit = this.authStore.hasPermission('employees.edit');
    const hasDelete = this.authStore.hasPermission('employees.delete');

    return [
      {
        id: '0.default',
        options:
          !exclude.includes('edit') &&
          hasEdit &&
          this.hasHierarchicalEditPermission(employee) &&
          employee.status.value !== EmployeeStatus.INVITED
            ? [
                {
                  name: `${this.translateService.instant('labels.edit')}...`,
                  value: () => this.edit(employee),
                  leftIcon: { name: 'edit-outlined', size: 20 },
                } as const,
              ]
            : [],
      },
      {
        id: '1.manage',
        options: [
          ...([EmployeeStatus.INVITED, EmployeeStatus.ARCHIVED].includes(
            employee.status.value,
          ) ||
          !this.fleetHelper.hasModule() ||
          !hasEdit ||
          !this.hasHierarchicalEditPermission(employee)
            ? []
            : [
                {
                  name: `${this.translateService.instant('labels.changeVehicle')}...`,
                  value: () => this.changeVehicles(employee),
                  leftIcon: { name: 'add-outlined', size: 20 },
                } as const,
              ]),
          ...(hasEdit &&
          this.hasHierarchicalEditPermission(employee) &&
          employee.status.value !== EmployeeStatus.INVITED
            ? [
                {
                  name: `${this.translateService.instant('unavailabilities.addUnavailability')}...`,
                  value: () =>
                    this.modalService.open<UnavailabilityModalData>(
                      UnavailabilityModalComponent,
                      {
                        mode: OnyxFormMode.ADD,
                        type: UnavailabilityType.EMPLOYEE,
                        item: employee,
                      },
                    ),
                  leftIcon: { name: 'vacation-outlined', size: 20 },
                } as const,
              ]
            : []),
        ],
      },
      {
        id: '2.archive',
        options: [
          ...(hasEdit &&
          this.hasHierarchicalEditPermission(employee) &&
          employee.status.value === EmployeeStatus.ARCHIVED
            ? [
                {
                  name: 'labels.unarchive',
                  value: () => this.unarchive(employee.uuid),
                  leftIcon: { name: 'undo-outlined', size: 20 },
                } as const,
              ]
            : []),
          ...(this.hasHierarchicalEditPermission(employee)
            ? [
                ...([EmployeeStatus.INVITED, EmployeeStatus.ARCHIVED].includes(
                  employee.status.value,
                )
                  ? hasDelete
                    ? [
                        {
                          name: 'labels.delete',
                          value: () => this.delete(employee, options),
                          leftIcon: { name: 'delete-outlined', size: 20 },
                          leftIconColor: 'red',
                        } as const,
                      ]
                    : []
                  : hasEdit
                    ? [
                        {
                          name: 'labels.archive',
                          value: () => this.archiveEmployee(employee),
                          leftIcon: { name: 'archives-outlined', size: 20 },
                          leftIconColor: 'red',
                        } as const,
                      ]
                    : []),
              ]
            : []),
        ],
      },
    ].filter((group) => group.options.length !== 0);
  }

  public openModal(employee: EmployeeModalData): void {
    this.modalService.open<EmployeeModalData>(EmployeeModalComponent, employee);
  }

  public edit(employee: Employee): void {
    this.modalService.open<EditEmployeeModalData>(
      EditEmployeeModalComponent,
      employee,
    );
  }

  public changeVehicles(employee: Employee): void {
    this.modalService.open<EmployeeAssignVehiclesModalData>(
      EmployeeAssignVehiclesModalComponent,
      employee,
    );
  }

  public hasHierarchicalEditPermission(employee: Employee): boolean {
    const userRoles = this.authStore.user()?.roles ?? [];
    if (userRoles.includes(EmployeeRole.BUSINESS_ADMIN)) return true;

    const employeeRoles = employee.roles.map((role) => role.value);
    if (employeeRoles.includes(EmployeeRole.BUSINESS_ADMIN)) return false;
    if (employeeRoles.includes(EmployeeRole.MANAGER)) return false;

    return userRoles.includes(EmployeeRole.MANAGER);
  }

  private unarchive(uuid: string): void {
    this.employeesService.unarchiveEmployee(uuid).subscribe(() =>
      this.toastService.showSuccess({
        message: 'employees.toasts.employeeUnarchived',
      }),
    );
  }

  private archiveEmployee(employee: Employee): void {
    if (employee.assignedVehicles.length > 0) {
      this.modalService
        .open<OnyxConfirmModalData, boolean>(OnyxConfirmModalComponent, {
          heading: `${this.translateService.instant(
            `${this.I18N}.hasAssignedVehicles`,
            {
              count: employee.assignedVehicles.length,
              name: `${employee.firstName} ${employee.lastName}`,
            },
          )}`,
          message: 'labels.continueArchive',
          confirmButtonText: 'labels.archive',
          action: () => this.employeesService.archiveEmployee(employee.uuid),
        })
        .result.subscribe((ok) => {
          if (ok) this.showArchived();
        });
    } else {
      this.employeesService
        .archiveEmployee(employee.uuid)
        .subscribe(() => this.showArchived());
    }
  }

  private showArchived(): void {
    this.toastService.showSuccess({
      message: 'employees.toasts.employeeArchived',
    });
  }

  private delete(employee: Employee, options: HelperOptions): void {
    this.modalService
      .open<OnyxConfirmModalData, boolean>(OnyxConfirmModalComponent, {
        heading: this.translateService.instant('employees.delete.heading', {
          employee:
            employee.status.value !== EmployeeStatus.INVITED
              ? this.namePipe.transform(employee)
              : employee.email,
        }),
        message: 'employees.delete.message',
        confirmButtonText: 'labels.delete',
        confirmButtonColor: 'red',
        action: () => this.employeesService.deleteEmployee(employee.uuid),
      })
      .result.subscribe((ok) => {
        if (!ok) return;

        this.toastService.showSuccess({
          message: 'employees.toasts.employeeDeleted',
        });
        CommonHelper.handleOptions(employee, options);
      });
  }

  public static getCoveringRole(
    role: EmployeeRole,
    roles: EmployeeRole[],
  ): EmployeeRole | null {
    const levelIndex = EMPLOYEE_ROLE_LEVELS.findIndex((level) =>
      level.roles.includes(role),
    );

    return (
      EMPLOYEE_ROLE_LEVELS.slice(0, levelIndex)
        .flatMap((level) => level.roles)
        .find((higherRole) => roles.includes(higherRole)) ?? null
    );
  }

  public static isArchived(employee: Employee | EmployeeStatus): boolean {
    const status = isString(employee) ? employee : employee.status.value;
    return status === EmployeeStatus.ARCHIVED;
  }

  public static isEmployeeType(
    employee: SimplifiedEmployee | Employee,
  ): employee is Employee {
    return 'email' in employee;
  }
}
